import React, { Fragment, useState } from "react"
import { Modal, Alert } from "react-bootstrap"
import { TextField } from "@mui/material"

import Constants from "../config.js"
import { getLog } from "../helpers/helpers.js"
import { downloadEncryptedBackupFile } from "../helpers/backup_file_download.js"
import { decryptMnemonic, isArgon2Blob } from "../helpers/wallet_crypto.js"
import { ButtonPrimary, ButtonPrimaryOutlined } from "./Buttons.js"
import { Loader } from "./Loader.js"


/**
 * BackupChoiceModal — a non-dismissible modal that forces the user to make
 * an explicit choice: download an encrypted backup file, or skip.
 *
 * Non-dismissible: backdrop="static" + keyboard={false} + no close button.
 * The two buttons (download / skip) are the only way out.
 *
 * Post-download verify step:
 *   - Only shown if passwordWasGenerated is true (generated passwords are
 *     more likely to be copy-pasted wrong, so verifying makes sense).
 *   - Re-enters the password → decrypts the just-downloaded blob in memory
 *     → confirms the file is usable.
 *   - Always skippable via a "skip this check" link.
 *
 * Props:
 *   - show: boolean (controlled by parent)
 *   - password: string (the password the user entered on the page)
 *   - walletname: string
 *   - address_owner: string
 *   - passwordWasGenerated: boolean (whether the password came from the generator)
 *   - generateBackupFile: (password) => Promise<object> (async, returns blob)
 *   - onComplete: (downloaded: boolean) => void  (called when user is done:
 *       downloaded=true if they downloaded a backup, false if they skipped.
 *       The parent handles save_session + redirect.)
 */
const BackupChoiceModal = ({
	show,
	password,
	walletname,
	address_owner,
	passwordWasGenerated,
	generateBackupFile,
	onComplete,
}) => {
	const log = getLog("BackupChoiceModal: ")

	// State machine: "default" → "downloading" → "downloaded" →
	// ("verify" if generated) → ("verify_success" | "verify_failed") → onComplete
	// or "default" → "skip_confirm" → onComplete
	const [step, setStep] = useState("default")
	const [error, setError] = useState("")
	const [verifyPassword, setVerifyPassword] = useState("")
	const [lastBlob, setLastBlob] = useState(null) // the blob from generateBackupFile, kept for verify

	const handleDownload = async () => {
		setStep("downloading")
		setError("")

		try {
			// generate the blob first (we need it for the verify step)
			const blob = await generateBackupFile(password)
			setLastBlob(blob)

			const result = await downloadEncryptedBackupFile(
				password,
				walletname,
				address_owner,
				async () => blob, // pass the already-generated blob
			)

			if (!result.success) {
				setStep("default")
				setError(result.error || "Download failed.")
				return
			}

			log("download success")

			// if the password was generated, offer the verify step;
			// otherwise go straight to "downloaded" (continue to wallet)
			if (passwordWasGenerated) {
				setStep("verify")
			} else {
				setStep("downloaded")
			}
		} catch (err) {
			log("download error:", err)
			setStep("default")
			setError(String(err && err.message ? err.message : err))
		}
	}

	const handleVerify = async () => {
		if (!verifyPassword) {
			setError("Please enter your password.")
			return
		}
		if (!lastBlob) {
			setError("Something went wrong — no backup to verify. Please re-download.")
			setStep("default")
			return
		}

		setError("")

		try {
			let decrypted = null
			if (isArgon2Blob(lastBlob)) {
				decrypted = await decryptMnemonic(lastBlob, verifyPassword)
			} else {
				// legacy path (shouldn't happen for new blobs, but kept for safety)
				const CryptoJS = require("crypto-js")
				const bytes = CryptoJS.AES.decrypt(lastBlob.encrypted_mnemonic, verifyPassword)
				decrypted = bytes.toString(CryptoJS.enc.Utf8)
			}

			if (decrypted && decrypted.length > 0) {
				log("verify success")
				setStep("verify_success")
			} else {
				log("verify failed (wrong password)")
				setStep("verify_failed")
			}
		} catch (err) {
			log("verify error:", err)
			setStep("verify_failed")
		}
	}

	const handleSkip = () => {
		setStep("skip_confirm")
	}

	const handleSkipConfirm = () => {
		onComplete(false)
		// reset for next time
		setStep("default")
		setError("")
		setVerifyPassword("")
		setLastBlob(null)
	}

	const handleContinueToWallet = () => {
		onComplete(true)
		// reset
		setStep("default")
		setError("")
		setVerifyPassword("")
		setLastBlob(null)
	}

	const handleRedownload = () => {
		setStep("default")
		setError("")
		setVerifyPassword("")
		setLastBlob(null)
	}

	const handleBack = () => {
		setStep("default")
		setError("")
		setVerifyPassword("")
	}

	const handleClose = () => {
		// no-op — modal is non-dismissible. The only way out is via
		// the buttons (download / skip). This prevents accidental dismissal.
	}

	return (
		<Modal
			show={show}
			onHide={handleClose}
			backdrop="static"
			keyboard={false}
			centered
			size="lg"
		>
			<Modal.Body style={{ padding: "24px", textAlign: "center", color: "white" }}>
				<h3>{Constants.BACKUP_CHOICE_TITLE}</h3>
				<br />

				{/* --- DEFAULT: two buttons --- */}
				{step === "default" ? (
					<Fragment>
						{error ? (
							<Fragment>
								<Alert variant="danger" style={{ fontSize: "14px" }}>{error}</Alert>
								<br />
							</Fragment>
						) : null}

						<div style={{ display: "flex", flexDirection: "column", gap: "12px", alignItems: "center" }}>
							<ButtonPrimary
								onClick={handleDownload}
								style={{ borderRadius: "10em", padding: "10px 24px", width: "80%" }}
							>
								<i className="fas fa-download" /> &nbsp; {Constants.BACKUP_CHOICE_DOWNLOAD}
							</ButtonPrimary>

							<ButtonPrimaryOutlined
								onClick={handleSkip}
								style={{ borderRadius: "10em", padding: "10px 24px", width: "80%" }}
							>
								{Constants.BACKUP_CHOICE_SKIP}
							</ButtonPrimaryOutlined>
						</div>
					</Fragment>
				) : null}

				{/* --- DOWNLOADING --- */}
				{step === "downloading" ? (
					<Fragment>
						<p>{Constants.BACKUP_CHOICE_DOWNLOADING}</p>
						<br />
						<Loader small />
					</Fragment>
				) : null}

				{/* --- DOWNLOADED (no verify needed, password was typed manually) --- */}
				{step === "downloaded" ? (
					<Fragment>
						<Alert variant="success" style={{ fontSize: "16px" }}>
							<i className="fa fa-check-circle" style={{ fontSize: "20px" }} /> &nbsp;
							{Constants.BACKUP_CHOICE_DOWNLOADED}
						</Alert>
						<br />
						<ButtonPrimary
							onClick={handleContinueToWallet}
							style={{ borderRadius: "10em", padding: "10px 24px" }}
						>
							{Constants.BACKUP_CHOICE_CONTINUE}
						</ButtonPrimary>
					</Fragment>
				) : null}

				{/* --- VERIFY (password was generated, re-enter to confirm) --- */}
				{step === "verify" ? (
					<Fragment>
						<Alert variant="info" style={{ fontSize: "14px", textAlign: "left" }}>
							{Constants.BACKUP_CHOICE_VERIFY_PROMPT}
						</Alert>
						<br />
						<div style={{ display: "flex", justifyContent: "center" }}>
							<TextField
								type="password"
								variant="outlined"
								value={verifyPassword}
								onChange={(e) => { setVerifyPassword(e.target.value); setError("") }}
								onKeyDown={(e) => { if (e.key === "Enter") handleVerify() }}
								label="Password"
								style={{ width: "300px" }}
								error={!!error}
								helperText={error || ""}
							/>
						</div>
						<br />
						<ButtonPrimary
							onClick={handleVerify}
							disabled={!verifyPassword}
							style={{
								borderRadius: "10em", padding: "8px 20px",
								pointerEvents: verifyPassword ? "auto" : "none",
								backgroundColor: verifyPassword ? "" : "gray",
							}}
						>
							Verify
						</ButtonPrimary>
						<br /><br />
						{/* skip this check — never blocks */}
						<span
							onClick={handleContinueToWallet}
							style={{
								fontSize: "13px", color: "grey", cursor: "pointer",
								textDecoration: "underline",
							}}
						>
							{Constants.BACKUP_CHOICE_VERIFY_SKIP}
						</span>
					</Fragment>
				) : null}

				{/* --- VERIFY SUCCESS --- */}
				{step === "verify_success" ? (
					<Fragment>
						<Alert variant="success" style={{ fontSize: "16px" }}>
							<i className="fa fa-check-circle" style={{ fontSize: "20px" }} /> &nbsp;
							{Constants.BACKUP_CHOICE_VERIFY_SUCCESS}
						</Alert>
						<br />
						<ButtonPrimary
							onClick={handleContinueToWallet}
							style={{ borderRadius: "10em", padding: "10px 24px" }}
						>
							{Constants.BACKUP_CHOICE_CONTINUE}
						</ButtonPrimary>
					</Fragment>
				) : null}

				{/* --- VERIFY FAILED --- */}
				{step === "verify_failed" ? (
					<Fragment>
						<Alert variant="danger" style={{ fontSize: "14px" }}>
							{Constants.BACKUP_CHOICE_VERIFY_FAILED}
						</Alert>
						<br />
						<div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
							<ButtonPrimaryOutlined
								onClick={handleRedownload}
								style={{ borderRadius: "10em", padding: "8px 20px" }}
							>
								{Constants.BACKUP_CHOICE_REDOWNLOAD}
							</ButtonPrimaryOutlined>
							<ButtonPrimaryOutlined
								onClick={handleContinueToWallet}
								style={{ borderRadius: "10em", padding: "8px 20px" }}
							>
								{Constants.BACKUP_CHOICE_CONTINUE_ANYWAY}
							</ButtonPrimaryOutlined>
						</div>
					</Fragment>
				) : null}

				{/* --- SKIP CONFIRM --- */}
				{step === "skip_confirm" ? (
					<Fragment>
						<Alert variant="warning" style={{ fontSize: "14px", textAlign: "left" }}>
							<i className="fa fa-exclamation-triangle" style={{ fontSize: "18px" }} /> &nbsp;
							{Constants.BACKUP_CHOICE_SKIP_CONFIRM}
						</Alert>
						<br />
						<div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
							<ButtonPrimaryOutlined
								onClick={handleBack}
								style={{ borderRadius: "10em", padding: "8px 20px" }}
							>
								Back
							</ButtonPrimaryOutlined>
							<ButtonPrimary
								onClick={handleSkipConfirm}
								style={{ borderRadius: "10em", padding: "8px 20px" }}
							>
								{Constants.BACKUP_CHOICE_SKIP}
							</ButtonPrimary>
						</div>
					</Fragment>
				) : null}

			</Modal.Body>
		</Modal>
	)
}

export default BackupChoiceModal
